import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import papi, { formatErr } from "@/lib/partnerApi";
import PartnerNav from "@/pages/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Gift, Tag } from "lucide-react";

export default function AdminPortal() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([papi.get("/partner/categories"), papi.get("/partner/rewards-config")]);
      setCats(c.data); setRewards(r.data);
    } catch (e) { if (e.response?.status === 401 || e.response?.status === 403) navigate("/login"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (localStorage.getItem("partner_role") !== "admin") { navigate("/login"); return; } load(); }, []);

  const add = async () => {
    if (!newCat.trim()) return;
    try { await papi.post("/partner/categories", { name: newCat }); setNewCat(""); toast.success("Category added"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const saveEdit = async () => { await papi.put(`/partner/categories/${editing.id}`, { name: editing.name }); setEditing(null); toast.success("Updated"); load(); };
  const del = async (id) => { await papi.delete(`/partner/categories/${id}`); toast.success("Removed"); load(); };
  const saveRewards = async () => { await papi.put("/partner/rewards-config", rewards); toast.success("Rewards updated"); };

  if (loading || !rewards) return <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;

  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <PartnerNav />
      <main className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-premium rounded-2xl p-6">
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Tag className="h-5 w-5 text-[#0E9AC8]" /> Vendor Categories <span className="text-sm text-slate-400 font-normal">({cats.length})</span></h2>
          <div className="flex gap-2 mt-4">
            <Input data-testid="new-cat" className="rounded-xl" placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            <Button data-testid="add-cat" onClick={add} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-4 divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
            {cats.map((c) => (
              <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-center justify-between py-2.5">
                {editing?.id === c.id ? (
                  <><Input className="rounded-lg h-9" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    <div className="flex gap-1 ml-2"><Button size="sm" onClick={saveEdit} className="bg-[#0E9AC8] text-white rounded-lg h-9">Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-9">Cancel</Button></div></>
                ) : (
                  <><span className="text-sm text-slate-700">{c.name}</span>
                    <div className="flex gap-1"><button onClick={() => setEditing({ id: c.id, name: c.name })} className="text-slate-400 hover:text-[#0E9AC8] p-1.5"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(c.id)} data-testid={`del-cat-${c.id}`} className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="h-4 w-4" /></button></div></>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card-premium rounded-2xl p-6 h-fit">
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Gift className="h-5 w-5 text-[#F5923C]" /> Reward Config</h2>
          <div className="mt-4 space-y-3">
            {rewards.milestones.map((m, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center gap-2"><span className="font-display font-bold text-[#0E9AC8] w-12">{m.pct}%</span>
                  <Input className="rounded-lg h-9 text-sm" value={m.title} onChange={(e) => { const ms = [...rewards.milestones]; ms[i] = { ...m, title: e.target.value }; setRewards({ ...rewards, milestones: ms }); }} /></div>
                <Input className="rounded-lg h-9 text-sm mt-2" placeholder="Reward (e.g. 2 FREE Premium Leads)" value={m.reward} onChange={(e) => { const ms = [...rewards.milestones]; ms[i] = { ...m, reward: e.target.value }; setRewards({ ...rewards, milestones: ms }); }} />
              </div>
            ))}
            <div><label className="text-xs text-slate-500">Free leads on 100% completion</label>
              <Input type="number" data-testid="free-leads" className="rounded-xl mt-1.5" value={rewards.free_leads_on_complete} onChange={(e) => setRewards({ ...rewards, free_leads_on_complete: Number(e.target.value) })} /></div>
            <Button data-testid="save-rewards" onClick={saveRewards} className="w-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl">Save Rewards</Button>
          </div>
        </div>
      </main>
    </div>
  );
}
